import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../utils/apiUtils";
import { clearCorporateSession, getCorporateToken } from "../utils/corporateSession";

const CorporateApprovals = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const [bookings, setBookings] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchMe = async () => {
    try {
      const res = await api.get("/corporate/me");
      if (res?.data?.success) {
        setMe(res.data.data);
      } else {
        toast.error(res?.data?.message || "Failed to load profile");
        clearCorporateSession();
        navigate("/corporate/login", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load profile");
      clearCorporateSession();
      navigate("/corporate/login", { replace: true });
    }
  };

  const fetchPending = async () => {
    try {
      const res = await api.get("/corporate/bookings?status=pending_corporate_approval");
      if (res?.data?.success) {
        setBookings(res.data.data || []);
      } else {
        toast.error(res?.data?.message || "Failed to load bookings");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load bookings");
    }
  };

  useEffect(() => {
    const token = getCorporateToken();
    if (!token) {
      navigate("/corporate/login", { replace: true });
      return;
    }

    (async () => {
      setLoading(true);
      await fetchMe();
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (me?.role === "admin") {
      fetchPending();
    }
  }, [me?.role]);

  const approve = async (id) => {
    setActionLoading(true);
    try {
      const res = await api.post(`/corporate/bookings/${id}/approve`, {});
      if (res?.data?.success) {
        toast.success(res.data.message || "Approved");
        await fetchPending();
      } else {
        toast.error(res?.data?.message || "Failed to approve");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to approve");
    } finally {
      setActionLoading(false);
    }
  };

  const reject = async (id) => {
    setActionLoading(true);
    try {
      const res = await api.post(`/corporate/bookings/${id}/reject`, {});
      if (res?.data?.success) {
        toast.success(res.data.message || "Rejected");
        await fetchPending();
      } else {
        toast.error(res?.data?.message || "Failed to reject");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to reject");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-600">Please login again.</div>
      </div>
    );
  }

  if (me.role !== "admin") {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="text-gray-800 font-semibold">Not authorized</div>
        <div className="text-sm text-gray-600">Only corporate admin can approve or reject bookings.</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Booking Approvals</h1>
          <div className="text-sm text-gray-600">Pending corporate approvals</div>
        </div>
        <button
          onClick={fetchPending}
          className={`px-4 py-2 rounded-md ${actionLoading ? "bg-gray-200 text-gray-600" : "bg-gray-900 text-white hover:bg-gray-800"}`}
          disabled={actionLoading}
        >
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        {bookings.length === 0 ? (
          <div className="text-sm text-gray-600">No bookings pending approval.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2">Pickup</th>
                  <th className="py-2">Drop</th>
                  <th className="py-2">Pickup Date</th>
                  <th className="py-2">Cab</th>
                  <th className="py-2">Amount</th>
                  <th className="py-2">Staff</th>
                  <th className="py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b._id} className="border-t hover:bg-gray-50">
                    <td className="py-2">{b.pickupLocation?.name || b.from || ""}</td>
                    <td className="py-2">{b.dropLocation?.name || b.to || ""}</td>
                    <td className="py-2">{b.pickupDate ? new Date(b.pickupDate).toLocaleDateString("en-IN") : ""} {b.pickupTime || ""}</td>
                    <td className="py-2">{b.cabType?.name || ""}</td>
                    <td className="py-2">₹{b.totalAmount || 0}</td>
                    <td className="py-2">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">
                          {b.corporateMemberId?.fullName || b.corporateMemberId?.email || ""}
                        </span>
                        {(b.corporateMemberId?.mobileNumber || b.corporateMemberId?.designation) && (
                          <span className="text-xs text-gray-600">
                            {b.corporateMemberId?.mobileNumber ? b.corporateMemberId.mobileNumber : ""}
                            {b.corporateMemberId?.mobileNumber && b.corporateMemberId?.designation ? " • " : ""}
                            {b.corporateMemberId?.designation ? b.corporateMemberId.designation : ""}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2">
                      <div className="flex gap-2">
                        <button
                          className="px-3 py-1 rounded-md bg-green-600 text-white hover:bg-green-700"
                          disabled={actionLoading}
                          onClick={() => approve(b._id)}
                          type="button"
                        >
                          Approve
                        </button>
                        <button
                          className="px-3 py-1 rounded-md bg-[#F56753] text-white hover:bg-[#d94e3a]"
                          disabled={actionLoading}
                          onClick={() => reject(b._id)}
                          type="button"
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CorporateApprovals;
